import { AxiosInstance } from 'axios';
import { 
  User, 
  LoginCredentials, 
  OTPValidation, 
  AuthResponse, 
  OTPResponse,
  AuthServiceInterface,
  UserRole,
  ValidateTokenResponse,
  CompleteRegistrationData
} from '@/types/auth';
import { env } from '@/config/environment';
import { apiRoutes } from '@/config/routes';
import { isTokenExpired } from '@/utils/jwtUtils';

/**
 * Create authentication service
 * @param apiService - Axios instance
 * @returns Auth service interface
 */
export const createAuthService = (apiService: AxiosInstance): AuthServiceInterface => {
  /**
   * Set authorization token
   */
  const setToken = (token: string): void => {
    (apiService as any).setToken(token);
  };

  /**
   * Clear authorization token
   */
  const clearToken = (): void => {
    (apiService as any).clearToken();
  };

  /**
   * Login with username and password
   * @param credentials - Login credentials
   * @returns Promise<OTPResponse>
   */
  const login = async (credentials: LoginCredentials): Promise<OTPResponse> => {
    try {
      const response = await apiService.post(apiRoutes.auth.login, credentials);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Validate OTP and complete login
   * @param otpData - OTP validation data
   * @returns Promise<AuthResponse> - Includes organization if available
   */
  const validateOTP = async (otpData: OTPValidation): Promise<AuthResponse> => {
    try {
      const response = await apiService.post(apiRoutes.auth.validateOtp, otpData);
      
      // Handle both wrapped and direct response structures
      const responseData = response.data.data || response.data;
      
      // Store token if OTP validation is successful
      if (responseData.access_token) {
        setToken(responseData.access_token);
      }
      
      // Organization data is handled by AuthContext.login() method
      // which receives this AuthResponse
      
      return responseData;
    } catch (error) {
      console.error('OTP validation error:', error);
      throw error;
    }
  };

  /**
   * Validate JWT token and get user details
   * @param token - JWT token to validate
   * @returns Promise<ValidateTokenResponse> - Includes user data and organization if available
   */
  const validateToken = async (token: string): Promise<ValidateTokenResponse> => {
    try {
      const response = await apiService.post(apiRoutes.auth.validateToken, { token });
      
      
      const userData = response.data.data || response.data;
      
      // If token is invalid, logout and throw error
      if (userData && userData.valid === false) {
        await logout();
        throw new Error('Token validation failed: token is invalid');
      }
      
      // Store user data in localStorage (includes organization if present)
      if (userData && typeof window !== 'undefined') {
        localStorage.setItem(env.USER_STORAGE_KEY, JSON.stringify(userData));
        // Organization is stored separately by AuthContext for easier access
        if (userData.organization) {
          localStorage.setItem(env.ORGANIZATION_STORAGE_KEY, JSON.stringify(userData.organization));
        }
      }
      
      return userData;
    } catch (error) {
      console.error('Token validation error:', error);
      throw error;
    }
  };

  /**
   * Get current user from stored token
   * @returns Promise<User | null>
   */
  const getCurrentUser = async (): Promise<User | null> => {
    try {
      if (typeof window === 'undefined') return null;
      
      const token = localStorage.getItem(env.JWT_STORAGE_KEY);
      if (!token) return null;
      
      return await validateToken(token);
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  };

  /**
   * Forgot password - send reset link to email
   * @param email - User email address
   * @returns Promise<void>
   */
  const forgotPassword = async (email: string): Promise<void> => {
    try {
      await apiService.post(apiRoutes.auth.forgotPassword, { email });
    } catch (error) {
      throw error;
    }
  };

  /**
   * Reset password with token
   * @param data - Reset password data with token
   * @returns Promise<AuthResponse>
   */
  const resetPasswordWithToken = async (data: {
    token: string;
    email: string;
    new_password: string;
    confirm_password: string;
  }): Promise<AuthResponse> => {
    try {
      const response = await apiService.post(apiRoutes.auth.resetPassword, data);
      
      const responseData = response.data.data || response.data;
      
      // Store token if reset is successful and auto-login happens
      if (responseData?.access_token) {
        setToken(responseData.access_token);
      }
      
      // Return the appropriate data structure
      return responseData;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  /**
   * Complete user registration with token and temporary password
   * @param data - Complete registration data with token, temp password, and new password
   * @returns Promise<AuthResponse>
   */
  const completeRegistration = async (data: CompleteRegistrationData): Promise<AuthResponse> => {
    try {
      const response = await apiService.post(apiRoutes.auth.completeRegistration, data);
      
      const responseData = response.data.data || response.data;
      
      // Store token if registration is successful and auto-login happens
      if (responseData?.access_token) {
        setToken(responseData.access_token);
      }
      
      return responseData;
    } catch (error) {
      console.error('Complete registration error:', error);
      throw error;
    }
  };

  /**
   * Logout user and clear stored data
   * Only calls logout API if there's a valid, non-expired token
   * This prevents unnecessary API calls on public screens or when no token exists
   * @returns Promise<void>
   */
  const logout = async (): Promise<void> => {
    try {
      // Check if there's a valid token before calling logout API
      // This prevents unnecessary API calls on public screens or when no token exists
      let hasValidToken = false;
      if (typeof window !== 'undefined') {
        const storedToken = localStorage.getItem(env.JWT_STORAGE_KEY);
        hasValidToken = storedToken !== null && !isTokenExpired(storedToken);
      }
      
      // Only call logout API endpoint if we have a valid token
      if (hasValidToken) {
        try {
          await apiService.post(apiRoutes.auth.logout);
        } catch (apiError) {
          // Log API error but continue with local cleanup
          // This ensures user is logged out locally even if API call fails
          console.error('Logout API error:', apiError);
        }
      }
      
      // Clear token from API service
      clearToken();
      
      // Clear stored data
      if (typeof window !== 'undefined') {
        localStorage.removeItem(env.JWT_STORAGE_KEY);
        localStorage.removeItem(env.USER_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  /**
   * Check if user is authenticated
   * @returns boolean
   */
  const isAuthenticated = (): boolean => {
    if (typeof window === 'undefined') return false;
    
    const token = localStorage.getItem(env.JWT_STORAGE_KEY);
    if (!token) return false;
    
    // Check if token is expired
    return !isTokenExpired(token);
  };

  /**
   * Get stored token
   * @returns string | null
   */
  const getStoredToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(env.JWT_STORAGE_KEY);
  };

  /**
   * Get stored user data
   * @returns User | null
   */
  const getStoredUser = (): User | null => {
    if (typeof window === 'undefined') return null;
    
    try {
      const userData = localStorage.getItem(env.USER_STORAGE_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  };

  // Return the service interface
  return {
    login,
    validateOTP,
    validateToken,
    getCurrentUser,
    logout,
    isAuthenticated,
    getStoredToken,
    getStoredUser,
    setToken,
    clearToken,
    forgotPassword,
    resetPasswordWithToken,
    completeRegistration,
  };
};

// JWT utilities exports removed - using backend token validation instead

